/**
 * ADR-0232 key-change alerts with ADR-0236 acknowledgement: staff
 * acknowledge an alert with a note (server-side staff audit); acknowledged
 * alerts are hidden unless "Show acknowledged" is on.
 */

import { useState } from 'react';

import type { TrustHardeningSummary, TrustKeyAlertView } from '@sill/types/api';

import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
export const KEY_ALERT_NOTE_MAX = 500;

export interface KeyAlertsSectionProps {
  readonly h: TrustHardeningSummary;
  /** Absent → read-only (API without acknowledgement). */
  readonly onAck?: (alertId: number, note: string) => Promise<void>;
  readonly includeAcked?: boolean;
  readonly onToggleAcked?: (next: boolean) => void;
}

function AckForm({ alert, onAck }: { alert: TrustKeyAlertView; onAck: (id: number, note: string) => Promise<void> }): JSX.Element {
  const C = useC();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = note.trim();
  const btn: React.CSSProperties = {
    fontFamily: sansFont, fontSize: 12, padding: '4px 9px', borderRadius: 3, cursor: 'pointer',
    background: 'transparent', color: C.body, border: `1px solid ${C.border}`,
  };
  if (!open) {
    return <button type="button" style={btn} onClick={() => setOpen(true)} data-testid={`ack-open-${alert.alert_id}`}>Acknowledge</button>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={KEY_ALERT_NOTE_MAX}
        rows={2}
        aria-label="Acknowledgement note"
        placeholder="What did you check? (required)"
        data-testid={`ack-note-${alert.alert_id}`}
        style={{ fontFamily: sansFont, fontSize: 12, padding: 6, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3 }}
      />
      {error !== null ? <div role="alert" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg }}>{error}</div> : null}
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="button"
          disabled={busy || trimmed.length === 0}
          data-testid={`ack-submit-${alert.alert_id}`}
          style={{ ...btn, background: C.text, color: C.bg, border: `1px solid ${C.text}`, opacity: busy || trimmed.length === 0 ? 0.5 : 1 }}
          onClick={() => {
            setBusy(true);
            setError(null);
            onAck(alert.alert_id!, trimmed)
              .then(() => setOpen(false))
              .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'The acknowledgement did not save.'))
              .finally(() => setBusy(false));
          }}
        >
          Save
        </button>
        <button type="button" style={btn} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

export function KeyAlertsSection({ h, onAck, includeAcked = false, onToggleAcked }: KeyAlertsSectionProps): JSX.Element {
  const C = useC();
  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const, marginBottom: 8 };
  const th = { textAlign: 'left' as const, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const };
  const td = { padding: '6px 8px', color: C.body, borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' as const };
  const ackable = onAck !== undefined && h.unacked_key_alerts !== undefined;
  return (
    <>
      <div style={{ ...kicker, marginTop: 24, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span>Key-change alerts</span>
        {h.unacked_key_alerts !== undefined ? (
          <Pill tone={h.unacked_key_alerts > 0 ? 'amber' : 'neutral'}>{h.unacked_key_alerts} unacknowledged</Pill>
        ) : null}
        {ackable && onToggleAcked !== undefined ? (
          <label style={{ fontFamily: sansFont, fontSize: 12, textTransform: 'none', letterSpacing: 0, color: C.body, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={includeAcked} onChange={(e) => onToggleAcked(e.target.checked)} data-testid="show-acked" />
            Show acknowledged
          </label>
        ) : null}
      </div>
      <Card style={{ padding: 16 }} data-testid="hardening-key-alerts">
        {h.key_alerts.length === 0 ? (
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
            {ackable && !includeAcked ? 'No unacknowledged key changes.' : 'No key changes since pinning started.'}
          </div>
        ) : (
          <TableScroll minWidth={760}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
              <thead>
                <tr>{['When', 'Signer', 'Key', 'Change', 'State', ...(ackable ? ['Acknowledged'] : [])].map((x) => <th key={x} style={th}>{x}</th>)}</tr>
              </thead>
              <tbody>
                {h.key_alerts.map((a) => (
                  <tr key={`${a.alert_id ?? ''}|${a.signer_host}|${a.thumbprint}|${a.created_at}|${a.kind}`}>
                    <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{formatIsoCompact(a.created_at)}</td>
                    <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{a.signer_host}{a.bundled_signer ? ' · bundled' : ''}</td>
                    <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }} title={a.thumbprint}>{a.thumbprint.slice(0, 12)}…</td>
                    <td style={td}>{a.kind === 'key_added' ? 'Added' : 'Removed'}</td>
                    <td style={td}>
                      <Pill tone={a.state === 'trusted' ? 'lime' : a.state === 'pending' ? 'amber' : 'neutral'}>{a.state}</Pill>
                    </td>
                    {ackable ? (
                      <td style={td}>
                        {a.acked_at !== null && a.acked_at !== undefined ? (
                          <div style={{ fontSize: 12 }} data-testid={`acked-${a.alert_id}`}>
                            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{formatIsoCompact(a.acked_at)} · {a.acked_by}</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{a.ack_note}</div>
                          </div>
                        ) : a.alert_id !== undefined && onAck !== undefined ? (
                          <AckForm alert={a} onAck={onAck} />
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </Card>
    </>
  );
}
